/* eslint-disable max-len */
import { makeStyles } from '@mui/styles';
import Container from 'components/Container/Container';
import config from 'config';
import { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import PaperCheckIcon from 'components/Icons/PaperCheck';
import ManagedIcon from 'components/Icons/Managed';
import MigrationsIcon from 'components/Icons/Migrations';
import PeaceIcon from 'components/Icons/Peace';
import CareIcon from 'components/Icons/Care';
import PowerIcon from 'components/Icons/Power';
import MinimunQuantityIcon from 'components/Icons/Infraestructure';
import { Link } from 'react-router-dom';

import { Typography, Button, Grid, Paper } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';

const useStyles = makeStyles(
  (theme) => ({
    '@keyframes peaceicon': {
      '0%': {
        animationTimingFunction: 'ease-in, ease-in-out',
        transform: 'matrix(1,0,0,1,3,8)',
      },
      '50%': {
        animationTimingFunction: 'ease-in, ease-in-out',
        transform: 'matrix(1,0,0,1,3,17)',
      },
      '100%': {
        animationTimingFunction: 'ease-in, ease-in-out',
        transform: 'matrix(1,0,0,1,3,8)',
      },
    },
    bg: {
      display: 'flex',
      color: '#fff',
      backgroundColor: '#dcdcdc',
      backgroundImage: 'linear-gradient(-30deg, #00004d 0%, #5333ed 49%, #2cd4d9 100%)',
      minHeight: 800,
      width: 'calc(100% + 2px)',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
    },
    container100: {
      height: '100%',
    },
    containerFeatures: {
      position: 'relative',
      top: -100,
      marginBottom: -100,
    },
    paperFeatures: {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'spacing' does not exist on type 'Default... Remove this comment to see the full error message
      padding: theme.spacing(10),
    },
    typoWithIcon: {
      display: 'inline-flex',
    },
    iconsWithText: {
      fontSize: 28,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'spacing' does not exist on type 'Default... Remove this comment to see the full error message
      marginRight: theme.spacing(3),
    },
    containerSecond: {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'spacing' does not exist on type 'Default... Remove this comment to see the full error message
      marginTop: theme.spacing(12),
    },
    containerContentSecond: {
      textAlign: 'center',
    },
    peaceicon: {
      '& g > g > g > g:nth-child(2)': {
        animationName: '$peaceicon',
        animationDuration: '3s',
        animationIterationCount: 'infinite',
      },
    },
  }),
  { name: 'BringerMaintenance' }
);

const Maintenance = memo(
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'message' does not exist on type '{ child... Remove this comment to see the full error message
  ({ message }) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const title = t('public.maintenance.mode');
    // eslint-disable-next-line
    const description = t('public.maintenance.description');

    const ldjson = {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url: config.domain,
      name: config.app.title,
      alternateName: 'Bringer',
      image: `${config.domain}/bpx-logo-white.png`,
      sameAs: [
        'https://www.facebook.com/Bringer',
        'https://instagram.com/Bringer',
        'https://www.linkedin.com/company/Bringer/',
        'https://twitter.com/@Bringer',
      ],
    };

    const ldjsonOrg = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: config.app.title,
      legalName: 'Bringer CORP',
      email: `mailto:contact@${config.host}`,
      url: config.domain,
      logo: `${config.domain}/bpx-logo-white.png`,
      sameAs: [
        'https://www.facebook.com/Bringer',
        'https://instagram.com/Bringer',
        'https://www.linkedin.com/company/Bringer/',
        'https://twitter.com/@Bringer',
      ],
    };

    return (
      <>
        <Helmet>
          <title>{title}</title>
          <link rel="canonical" href={`${config.domain}/`} />
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="twitter:title" content={title} />
          <meta name="og:description" content={description} />
          <meta name="twitter:description" content={description} />
          <meta property="og:type" content="website" />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify(ldjson)}</script>
          <script type="application/ld+json">{JSON.stringify(ldjsonOrg)}</script>
        </Helmet>
        <div className={classes.bg}>
          <Container>
            <Grid container alignItems="center" className={classes.container100}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h3" component="h1" gutterBottom>
                  {t('public.maintenance.mode')}
                </Typography>
                <Typography variant="h5">{message}</Typography>
                <br />
                <Button component={Link} variant="contained" color="primary" to="/">
                  {t('public.maintenance.go.main.page')}
                </Button>
              </Grid>
            </Grid>
          </Container>
        </div>
        <div className={classes.containerFeatures}>
          <Container>
            <Paper className={classes.paperFeatures}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="h5">{t('public.maintenance.text.1')}</Typography>
                  <Typography variant="subtitle1">{t('public.maintenance.text.2')}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography gutterBottom className={classes.typoWithIcon}>
                    <PaperCheckIcon className={classes.iconsWithText} />
                    {t('public.maintenance.text.3')}
                  </Typography>
                  <Typography className={classes.typoWithIcon}>
                    <ManagedIcon className={classes.iconsWithText} />
                    {t('public.maintenance.text.4')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography gutterBottom className={classes.typoWithIcon}>
                    <MigrationsIcon className={classes.iconsWithText} />
                    {t('public.maintenance.text.5')}
                  </Typography>
                  <Typography className={classes.typoWithIcon}>
                    <MinimunQuantityIcon className={classes.iconsWithText} />
                    {t('public.maintenance.text.6')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Container>
        </div>
        {/* // @ts-expect-error ts-migrate(2786) FIXME: 'Container' cannot be used as a JSX component. */}
        <Container>
          {/* // @ts-expect-error ts-migrate(2322) FIXME: Type 'Element' is not assignable to type 'null
          | u... Remove this comment to see the full error message */}
          <Grid container className={classes.containerSecond}>
            <Grid item xs={12}>
              <Typography variant="h5" align="center">
                {t('public.maintenance.text.7')}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {t('public.maintenance.text.8')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item xs={12} sm={3} className={classes.containerContentSecond}>
                  <PeaceIcon className={classes.peaceicon} />
                  <Typography variant="h6" align="center">
                    {t('public.maintenance.text.9')}
                  </Typography>
                  <Typography variant="subtitle1" align="center">
                    {t('public.maintenance.text.10')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3} className={classes.containerContentSecond}>
                  <CareIcon />
                  <Typography variant="h6" align="center">
                    {t('public.maintenance.text.11')}
                  </Typography>
                  <Typography variant="subtitle1" align="center">
                    {t('public.maintenance.text.12')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3} className={classes.containerContentSecond}>
                  <PowerIcon />
                  <Typography variant="h6" align="center">
                    {t('public.maintenance.text.13')}
                  </Typography>
                  <Typography variant="subtitle1" align="center">
                    {t('public.maintenance.text.14')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
        <br />
        <br />
      </>
    );
  },
  () => true
);

export default Maintenance;
